import type { CoreEvent } from './choiceMaps';

export type Scene = {
  sceneId: string;
  category: 'repair' | 'boundary' | 'group' | 'avoidance' | 'meme' | 'pressure';
  medium:
    | 'direct_chat'
    | 'group_chat'
    | 'work_chat'
    | 'social_post'
    | 'voice_message'
    | 'live_conversation'
    | 'public_place'
    | 'meeting'
    | 'favor_request'
    | 'food_order'
    | 'meal';
  copy: {
    'zh-CN': string;
    en: string;
  };
  supportedEvents: CoreEvent[];
};

export const scenes: Scene[] = [
  {
    sceneId: 'friend_last_minute_cancel',
    category: 'repair',
    medium: 'direct_chat',
    copy: {
      'zh-CN': '朋友临时鸽了你，还发来一句“下次一定”。',
      en: 'A friend cancels at the last minute and sends: “Next time for sure.”'
    },
    supportedEvents: [
      'swipe_up_escape',
      'swipe_down_confront',
      'swipe_left_play_cool',
      'swipe_right_fix',
      'fast_fling_nope'
    ]
  },
  {
    sceneId: 'group_credit_repeat',
    category: 'group',
    medium: 'group_chat',
    copy: {
      'zh-CN': '群里有人把你刚说过的点又说了一遍，大家开始夸 TA。',
      en: 'Someone repeats the point you just made in the group chat, and everyone starts praising them.'
    },
    supportedEvents: [
      'long_press_suppress',
      'swipe_down_confront',
      'swipe_right_fix',
      'swipe_left_play_cool'
    ]
  },
  {
    sceneId: 'serious_message_flat_reply',
    category: 'avoidance',
    medium: 'direct_chat',
    copy: {
      'zh-CN': '你认真发了一大段，对方只回了一个“哈哈”。',
      en: 'You send a serious paragraph, and they reply with only “haha.”'
    },
    supportedEvents: [
      'timed_hesitation',
      'swipe_up_escape',
      'swipe_right_fix',
      'double_tap_meme',
      'long_press_suppress'
    ]
  },
  {
    sceneId: 'after_hours_work_ping',
    category: 'boundary',
    medium: 'work_chat',
    copy: {
      'zh-CN': '刚准备休息，工作群弹出“方便现在看一下吗”。',
      en: 'Just as you are about to rest, the work chat pops up: “Can you take a look now?”'
    },
    supportedEvents: [
      'swipe_up_escape',
      'swipe_down_confront',
      'swipe_right_fix',
      'swipe_left_play_cool',
      'long_press_suppress'
    ]
  },
  {
    sceneId: 'boundary_discomfort',
    category: 'boundary',
    medium: 'live_conversation',
    copy: {
      'zh-CN': '对方说“你想多了”，但你确实感到不舒服。',
      en: 'They say “you are overthinking,” but you really do feel uncomfortable.'
    },
    supportedEvents: [
      'swipe_right_fix',
      'swipe_down_confront',
      'long_press_suppress',
      'fast_fling_nope'
    ]
  },
  {
    sceneId: 'social_comment_side_eye',
    category: 'meme',
    medium: 'social_post',
    copy: {
      'zh-CN': '你刚发完朋友圈，熟人评论一句“最近很闲啊”。',
      en: 'You post a social update, and an acquaintance comments: “You seem pretty free lately.”'
    },
    supportedEvents: [
      'double_tap_meme',
      'swipe_left_play_cool',
      'swipe_right_fix',
      'swipe_up_escape',
      'shake_spiral'
    ]
  },
  {
    sceneId: 'public_queue_cut',
    category: 'boundary',
    medium: 'public_place',
    copy: {
      'zh-CN': '你排队很久，前面有人自然地插进来了。',
      en: 'You have been waiting in line for a long time, and someone casually cuts in front.'
    },
    supportedEvents: [
      'swipe_down_confront',
      'swipe_right_fix',
      'long_press_suppress',
      'fast_fling_nope'
    ]
  },
  {
    sceneId: 'just_be_direct_claim',
    category: 'repair',
    medium: 'live_conversation',
    copy: {
      'zh-CN': '朋友说“我说话直，你别介意”，然后继续让你不舒服。',
      en: 'A friend says, “I am just direct, don’t mind me,” then keeps making you uncomfortable.'
    },
    supportedEvents: [
      'swipe_right_fix',
      'swipe_down_confront',
      'swipe_left_play_cool',
      'shake_spiral'
    ]
  },
  {
    sceneId: 'group_chat_heating_up',
    category: 'group',
    medium: 'group_chat',
    copy: {
      'zh-CN': '群里开始争一个小事，气氛越来越热，你其实知道答案。',
      en: 'The group starts arguing over something small, and the mood heats up. You actually know the answer.'
    },
    supportedEvents: [
      'long_press_suppress',
      'swipe_right_fix',
      'swipe_down_confront',
      'double_tap_meme',
      'shake_spiral'
    ]
  },
  {
    sceneId: 'read_no_reply_social_proof',
    category: 'avoidance',
    medium: 'direct_chat',
    copy: {
      'zh-CN': '对方已读不回 6 小时，但刚刚发了动态。',
      en: 'They have left you on read for six hours, but just posted a new update.'
    },
    supportedEvents: [
      'timed_hesitation',
      'swipe_up_escape',
      'swipe_down_confront',
      'swipe_right_fix',
      'double_tap_meme'
    ]
  },
  {
    sceneId: 'meeting_silence_opinion',
    category: 'repair',
    medium: 'meeting',
    copy: {
      'zh-CN': '你准备提出不同意见，会议里突然安静到能听见空调声。',
      en: 'You are about to disagree, and the meeting suddenly gets quiet enough to hear the air conditioner.'
    },
    supportedEvents: [
      'swipe_down_confront',
      'swipe_right_fix',
      'long_press_suppress',
      'swipe_left_play_cool'
    ]
  },
  {
    sceneId: 'topic_takeover',
    category: 'group',
    medium: 'live_conversation',
    copy: {
      'zh-CN': '朋友连续三次把聊天变成 TA 的主场，你的话题又被带走了。',
      en: 'A friend turns the conversation back to themselves three times, and your topic gets carried away again.'
    },
    supportedEvents: [
      'fast_fling_nope',
      'swipe_right_fix',
      'swipe_down_confront',
      'swipe_left_play_cool'
    ]
  },
  {
    sceneId: 'small_task_expands',
    category: 'boundary',
    medium: 'favor_request',
    copy: {
      'zh-CN': '别人把“能不能帮个小忙”说得很轻，但实际要花你半天。',
      en: 'Someone frames it as “a tiny favor,” but it would actually take half your day.'
    },
    supportedEvents: [
      'swipe_right_fix',
      'swipe_down_confront',
      'swipe_up_escape',
      'long_press_suppress'
    ]
  },
  {
    sceneId: 'wrong_order_pressure',
    category: 'pressure',
    medium: 'food_order',
    copy: {
      'zh-CN': '你点了外卖备注“不要香菜”，打开后香菜很显眼。',
      en: 'Your order arrives with the wrong item. You are hungry now.'
    },
    supportedEvents: [
      'shake_spiral',
      'fast_fling_nope',
      'swipe_right_fix',
      'swipe_down_confront',
      'swipe_left_play_cool'
    ]
  },
  {
    sceneId: 'public_hot_take_cooldown',
    category: 'pressure',
    medium: 'social_post',
    copy: {
      'zh-CN': '你看到一个特别离谱的热搜，很想立刻转发点评。',
      en: 'Someone in your online community posts a hot take. The chat is watching.'
    },
    supportedEvents: [
      'long_press_suppress',
      'double_tap_meme',
      'swipe_down_confront',
      'swipe_up_escape',
      'shake_spiral'
    ]
  },
  {
    sceneId: 'borrowed_item_not_returned',
    category: 'boundary',
    medium: 'live_conversation',
    copy: {
      'zh-CN': '朋友借走东西说“明天还”，一周后你还没看到它。',
      en: 'A friend borrowed something and said “tomorrow.” One week later, it is still missing.'
    },
    supportedEvents: [
      'swipe_right_fix',
      'swipe_down_confront',
      'long_press_suppress',
      'swipe_up_escape'
    ]
  },
  {
    sceneId: 'vague_apology_non_apology',
    category: 'repair',
    medium: 'direct_chat',
    copy: {
      'zh-CN': '对方发来“如果你觉得受伤了，那我道歉”。',
      en: 'They send: “If you felt hurt, then I apologize.”'
    },
    supportedEvents: [
      'swipe_down_confront',
      'swipe_right_fix',
      'long_press_suppress',
      'shake_spiral'
    ]
  },
  {
    sceneId: 'friend_dumping_no_space',
    category: 'pressure',
    medium: 'live_conversation',
    copy: {
      'zh-CN': '朋友连续倾诉 40 分钟，却完全没问你现在有没有精力。',
      en: 'A friend vents for 40 minutes without asking whether you have the energy right now.'
    },
    supportedEvents: [
      'swipe_right_fix',
      'swipe_down_confront',
      'swipe_up_escape',
      'long_press_suppress',
      'fast_fling_nope'
    ]
  },
  {
    sceneId: 'group_plan_no_decider',
    category: 'group',
    medium: 'group_chat',
    copy: {
      'zh-CN': '周末计划聊了半天，没人定时间、地点和预算。',
      en: 'The weekend plan has gone on forever, but nobody has chosen a time, place, or budget.'
    },
    supportedEvents: [
      'swipe_right_fix',
      'swipe_down_confront',
      'double_tap_meme',
      'swipe_left_play_cool'
    ]
  },
  {
    sceneId: 'message_misread_in_group',
    category: 'repair',
    medium: 'group_chat',
    copy: {
      'zh-CN': '你一句中性提醒被群里理解成“在阴阳怪气”。',
      en: 'Your neutral reminder gets read by the group as a passive-aggressive jab.'
    },
    supportedEvents: [
      'swipe_right_fix',
      'double_tap_meme',
      'swipe_down_confront',
      'timed_hesitation'
    ]
  },
  {
    sceneId: 'bill_split_unfair',
    category: 'boundary',
    medium: 'meal',
    copy: {
      'zh-CN': '聚餐买单时，有人默认把 TA 多点的东西平摊给所有人。',
      en: 'At dinner, someone quietly splits the extra things they ordered across everyone.'
    },
    supportedEvents: [
      'swipe_down_confront',
      'swipe_right_fix',
      'long_press_suppress',
      'swipe_left_play_cool'
    ]
  },
  {
    sceneId: 'photo_tag_unflattering',
    category: 'meme',
    medium: 'social_post',
    copy: {
      'zh-CN': '朋友发合照还圈了你，但你在里面表情很想重开。',
      en: 'A friend posts a group photo and tags you, but your face in it needs a reset button.'
    },
    supportedEvents: [
      'double_tap_meme',
      'swipe_right_fix',
      'swipe_up_escape',
      'swipe_down_confront',
      'fast_fling_nope'
    ]
  },
  {
    sceneId: 'voice_message_wall',
    category: 'avoidance',
    medium: 'voice_message',
    copy: {
      'zh-CN': '对方连续发来 7 条 60 秒语音，开头都是“我简单说两句”。',
      en: 'They send seven 60-second voice notes in a row, each starting with “quickly.”'
    },
    supportedEvents: [
      'timed_hesitation',
      'swipe_up_escape',
      'swipe_down_confront',
      'swipe_right_fix',
      'double_tap_meme',
      'long_press_suppress'
    ]
  },
  {
    sceneId: 'public_correction_pressure',
    category: 'pressure',
    medium: 'public_place',
    copy: {
      'zh-CN': '你在公开场合说错一个细节，对方立刻当众纠正。',
      en: 'You get one detail wrong in public, and someone corrects you immediately in front of everyone.'
    },
    supportedEvents: [
      'shake_spiral',
      'swipe_left_play_cool',
      'swipe_right_fix',
      'swipe_down_confront'
    ]
  }
];
